/**
 * Junto Frontend PurchasePopup Component
 * 
 * This is the modular purchase popup that can be used by any studio.
 * It maintains exact visual equivalence to the Sol Room purchase popup while
 * being configurable for other studios through theme props.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, Button, TabGroup, Tab, TabPanel } from '../ui/index.js';
import { solRoomTheme } from '../../themes/sol-room.js';

const PurchaseContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  max-height: 500px;
  overflow-y: auto;
`;

const ProductCard = styled.div`
  padding: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
  border: 1px solid ${props => props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light};
  border-radius: 12px;
  background: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px ${props => props.theme?.shadows?.medium || solRoomTheme.shadows.medium};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -8px;
  right: 16px;
  background: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  color: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProductHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
`;

const ProductTitle = styled.h3`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm} 0;
`;

const ProductPrice = styled.div`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  margin-bottom: ${props => props.theme?.spacing?.xs || solRoomTheme.spacing.xs};
`;

const ProductPriceDetail = styled.div`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
`;

const ProductDescription = styled.div`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  line-height: 1.5;
  margin-bottom: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
  text-align: center;
`;

const ProductFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg} 0;
`;

const ProductFeature = styled.li`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin-bottom: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  padding-left: 16px;
  position: relative;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
    font-weight: bold;
  }
`;


const PurchasePopup = ({ 
  isOpen, 
  onClose, 
  onPurchase,
  item,
  itemType,
  // Legacy support for arrays (backward compatibility)
  creditPackages = [],
  memberships = [],
  maxWidth,
  isLoading = false,
  theme = solRoomTheme 
}) => {
  const [activeTab, setActiveTab] = useState('credits');
  
  // Debug logging
  console.log('PurchasePopup props:', { item, itemType, creditPackages, memberships });
  if (item) {
    console.log('Selected item fullDescription:', item.fullDescription);
  }

  const handlePurchase = async (product) => {
    if (onPurchase) {
      try {
        await onPurchase(product);
      } catch (error) {
        console.error('Purchase failed:', error);
      }
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const calculateMonthlyCost = (price, periodType) => {
    switch (periodType) {
      case 'monthly': return price;
      case 'quarterly': return price / 3;
      case 'annually': return price / 12;
      default: return price;
    }
  };


  // Handle single item case vs arrays case
  const isSingleItem = item && itemType;
  const effectiveCredits = isSingleItem && itemType === 'credit' ? [item] : creditPackages;
  const effectiveMemberships = isSingleItem && itemType === 'membership' ? [item] : memberships;
  
  // Determine if we should show tabs (only if both types have items and it's not a single item)
  const showTabs = !isSingleItem && effectiveCredits.length > 0 && effectiveMemberships.length > 0;
  const hasOnlyCredits = effectiveCredits.length > 0 && effectiveMemberships.length === 0;
  const hasOnlyMemberships = effectiveMemberships.length > 0 && effectiveCredits.length === 0;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      maxWidth={maxWidth || (isSingleItem ? "500px" : "800px")}
      theme={theme}
    >
      <PurchaseContainer theme={theme}>
        {showTabs && (
          <TabGroup theme={theme}>
            <Tab 
              active={activeTab === 'credits'} 
              onClick={() => setActiveTab('credits')}
              theme={theme}
            >
              Credits ({effectiveCredits.length})
            </Tab>
            <Tab 
              active={activeTab === 'memberships'} 
              onClick={() => setActiveTab('memberships')}
              theme={theme}
            >
              Memberships ({effectiveMemberships.length})
            </Tab>
          </TabGroup>
        )}

        <TabPanel theme={theme}>
          {showTabs && activeTab === 'credits' && (
            <ProductGrid theme={theme}>
              {effectiveCredits.map((pkg) => (
                <ProductCard key={pkg.id} theme={theme}>
                  {pkg.isPopular && <PopularBadge theme={theme}>Most Popular</PopularBadge>}
                  <ProductHeader theme={theme}>
                    <ProductTitle theme={theme}>{pkg.name}</ProductTitle>
                    <ProductPrice theme={theme}>{formatPrice(pkg.price)}</ProductPrice>
                    <ProductPriceDetail theme={theme}>{pkg.credits} credit{pkg.credits !== 1 ? 's' : ''}</ProductPriceDetail>
                  </ProductHeader>
                  {pkg.fullDescription && <ProductDescription theme={theme}>{pkg.fullDescription}</ProductDescription>}
                  {pkg.features && pkg.features.length > 0 && (
                    <ProductFeatures theme={theme}>
                      {pkg.features.map((feature, index) => (
                        <ProductFeature key={index} theme={theme}>{feature}</ProductFeature>
                      ))}
                    </ProductFeatures>
                  )}
                  <Button variant="filled" size="medium" fullWidth onClick={() => handlePurchase(pkg)} disabled={isLoading} theme={theme}>
                    Purchase
                  </Button>
                </ProductCard>
              ))}
            </ProductGrid>
          )}
          
          {showTabs && activeTab === 'memberships' && (
            <ProductGrid theme={theme}>
              {effectiveMemberships.map((membership) => (
                <ProductCard key={membership.id} theme={theme}>
                  {membership.isPopular && <PopularBadge theme={theme}>Most Popular</PopularBadge>}
                  <ProductHeader theme={theme}>
                    <ProductTitle theme={theme}>{membership.name}</ProductTitle>
                    <ProductPrice theme={theme}>{formatPrice(calculateMonthlyCost(membership.price, membership.periodType))}/month</ProductPrice>
                    <ProductPriceDetail theme={theme}>Billed {membership.periodType} - {formatPrice(membership.price)}</ProductPriceDetail>
                  </ProductHeader>
                  {membership.fullDescription && <ProductDescription theme={theme}>{membership.fullDescription}</ProductDescription>}
                  {membership.features && membership.features.length > 0 && (
                    <ProductFeatures theme={theme}>
                      {membership.features.map((feature, index) => (
                        <ProductFeature key={index} theme={theme}>{feature}</ProductFeature>
                      ))}
                    </ProductFeatures>
                  )}
                  <Button variant="filled" size="medium" fullWidth onClick={() => handlePurchase(membership)} disabled={isLoading} theme={theme}>
                    Subscribe
                  </Button>
                </ProductCard>
              ))}
            </ProductGrid>
          )}
          
          {(hasOnlyCredits || (isSingleItem && itemType === 'credit')) && (
            <ProductGrid theme={theme}>
              {effectiveCredits.map((pkg) => (
                <ProductCard key={pkg.id} theme={theme}>
                  {pkg.isPopular && <PopularBadge theme={theme}>Most Popular</PopularBadge>}
                  <ProductHeader theme={theme}>
                    <ProductTitle theme={theme}>{pkg.name}</ProductTitle>
                    <ProductPrice theme={theme}>{formatPrice(pkg.price)}</ProductPrice>
                    <ProductPriceDetail theme={theme}>{pkg.credits} credit{pkg.credits !== 1 ? 's' : ''}</ProductPriceDetail>
                  </ProductHeader>
                  {pkg.fullDescription && <ProductDescription theme={theme}>{pkg.fullDescription}</ProductDescription>}
                  {pkg.features && pkg.features.length > 0 && (
                    <ProductFeatures theme={theme}>
                      {pkg.features.map((feature, index) => (
                        <ProductFeature key={index} theme={theme}>{feature}</ProductFeature>
                      ))}
                    </ProductFeatures>
                  )}
                  <Button variant="filled" size="medium" fullWidth onClick={() => handlePurchase(pkg)} disabled={isLoading} theme={theme}>
                    Purchase
                  </Button>
                </ProductCard>
              ))}
            </ProductGrid>
          )}
          
          {(hasOnlyMemberships || (isSingleItem && itemType === 'membership')) && (
            <ProductGrid theme={theme}>
              {effectiveMemberships.map((membership) => (
                <ProductCard key={membership.id} theme={theme}>
                  {membership.isPopular && <PopularBadge theme={theme}>Most Popular</PopularBadge>}
                  <ProductHeader theme={theme}>
                    <ProductTitle theme={theme}>{membership.name}</ProductTitle>
                    <ProductPrice theme={theme}>{formatPrice(calculateMonthlyCost(membership.price, membership.periodType))}/month</ProductPrice>
                    <ProductPriceDetail theme={theme}>Billed {membership.periodType} - {formatPrice(membership.price)}</ProductPriceDetail>
                  </ProductHeader>
                  {membership.fullDescription && <ProductDescription theme={theme}>{membership.fullDescription}</ProductDescription>}
                  {membership.features && membership.features.length > 0 && (
                    <ProductFeatures theme={theme}>
                      {membership.features.map((feature, index) => (
                        <ProductFeature key={index} theme={theme}>{feature}</ProductFeature>
                      ))}
                    </ProductFeatures>
                  )}
                  <Button variant="filled" size="medium" fullWidth onClick={() => handlePurchase(membership)} disabled={isLoading} theme={theme}>
                    Subscribe
                  </Button>
                </ProductCard>
              ))}
            </ProductGrid>
          )}
        </TabPanel>
      </PurchaseContainer>
    </Modal>
  );
};

export default PurchasePopup;